<template>
  <div>
    <div
      class="border-b dark:border-gray-700 space-y-4 md:space-y-0 md:flex justify-between"
    >
      <slot name="header" />

      <template v-if="!disableSorting">
        <plugin-index-sort
          v-model:order-by="orderBy"
          v-model:direction="direction"
          class="pb-4"
          :loading="loading"
          @order-by-change="onOrderByChange"
        />
      </template>
    </div>

    <plugin-grid :plugins="plugins" :columns="columns" />

    <div v-if="plugins.length === 0 && !loadingBottom && !loading" class="mt-4">
      <p>No results.</p>
    </div>

    <c-spinner v-if="loadingBottom" />
  </div>
</template>

<script setup lang="ts">
  import PluginGrid from './PluginGrid.vue';
  import PluginIndexSort from './PluginIndexSort.vue';
  import {
    computed,
    onMounted,
    onUnmounted,
    ref,
    toRefs,
    usePluginIndexStore,
    usePluginStoreStore,
  } from '#imports';

  // Props

  const props = withDefaults(
    defineProps<{
      columns?: any[];
      action: string;
      requestData: object;
      disableSorting?: boolean;
    }>(),
    {
      columns: () => [],
      action: '',
      disableSorting: false,
    },
  );

  const {columns, action, requestData, disableSorting} = toRefs(props);

  // Data
  const orderBy = ref('');
  const direction = ref('');
  const loading = ref(false);
  const loadingBottom = ref(false);
  const page = ref(1);

  // Computed
  const pluginIndexStore = usePluginIndexStore();
  const pluginStoreStore = usePluginStoreStore();

  const plugins = computed(() => pluginIndexStore.plugins);
  const sortOptions = computed(() => pluginStoreStore.sortOptions);
  const hasMore = computed(() => pluginIndexStore.hasMore);

  const requestActionData = computed(() => {
    return {
      ...requestData.value,
      page: page.value,
      orderBy: disableSorting.value ? null : orderBy.value,
      direction: disableSorting.value ? null : direction.value,
    };
  });

  // Created
  const keys = Object.keys(sortOptions.value);
  const firstOptionKey = keys[0];

  // Mounted
  onMounted(() => {
    if (hasMore) {
      page.value++;

      if (!hasScrollBar()) {
        requestPlugins();
      }

      window.addEventListener('resize', onWindowResize);
      window.addEventListener('scroll', onScroll);
    }

    orderBy.value = firstOptionKey;
    direction.value = sortOptions.value[firstOptionKey];
  });

  // Destroyed
  onUnmounted(() => {
    window.removeEventListener('resize', onWindowResize);
    window.removeEventListener('scroll', onScroll);

    pluginIndexStore.cancelRequests();
  });

  // Methods
  const onOrderByChange = () => {
    pluginIndexStore.cancelRequests();

    requestPlugins(true);
  };

  const onScroll = () => {
    if (loading.value) {
      return null;
    }

    if (loadingBottom.value) {
      return null;
    }

    window.removeEventListener('scroll', onScroll);

    if (scrollDistFromBottom() < 300) {
      requestPlugins(false, () => {
        if (hasMore.value) {
          window.addEventListener('scroll', onScroll);
        }
      });
    } else {
      window.addEventListener('scroll', onScroll);
    }
  };

  const scrollDistFromBottom = () => {
    const $container = scrollContainer();
    const scrollTop = $container.scrollTop;
    const scrollHeight = $container.scrollHeight;
    const innerHeight = window.innerHeight;

    return scrollHeight - Math.max(scrollTop + innerHeight, 0);
  };

  const onWindowResize = () => {
    if (!hasMore.value) {
      return null;
    }

    if (hasScrollBar()) {
      return null;
    }

    requestPlugins();
  };

  const requestPlugins = (dontAppendData, onAfterSuccess) => {
    if (loading.value) {
      return null;
    }

    if (loadingBottom.value) {
      return null;
    }

    if (!dontAppendData && !hasMore.value) {
      return null;
    }

    if (dontAppendData) {
      page.value = 1;
      loading.value = true;
    } else {
      loadingBottom.value = true;
    }

    pluginIndexStore[action.value]({
      ...requestActionData.value,
      appendData: !dontAppendData,
    })
      .then((response) => {
        loading.value = false;
        loadingBottom.value = false;

        if (hasMore.value) {
          page.value++;

          if (!hasScrollBar()) {
            requestPlugins();
          }
        }

        if (typeof onAfterSuccess === 'function') {
          onAfterSuccess(response);
        }
      })
      .catch(() => {
        loading.value = false;
        loadingBottom.value = false;
      });
  };

  const scrollContainer = () => {
    return document.documentElement;
  };

  const hasScrollBar = () => {
    const $scrollContainer = scrollContainer();

    if ($scrollContainer.clientHeight < $scrollContainer.scrollHeight) {
      return true;
    }
    return false;
  };
</script>

<style lang="scss">
  .xcontainer.page-leave {
    display: none;
  }
</style>
